/**
 * AuthApiClient
 * 
 * Handles all authentication-related API operations
 */

import { BaseApiClient } from './BaseApiClient';
import type { User } from '../../types/api/user';

export class AuthApiClient extends BaseApiClient {
  /**
   * Login with username/email and password
   */
  async login(usernameOrEmail: string, password: string): Promise<{ username: string; token: string }> {
    // Detect if input is an email or username
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(usernameOrEmail);
    
    const payload = isEmail 
      ? { email: usernameOrEmail, password }
      : { username: usernameOrEmail, password };
    
    try {
      // Clear any existing token before login to prevent stale token issues
      const existingToken = this.getToken();
      if (existingToken) {
        console.log('🔄 Clearing existing token before login attempt');
        this.removeToken();
      }
      
      const result = await this.post<{ username: string; token: string }>('/login', payload);
      this.setToken(result.token);
      return result;
    } catch (error) {
      // If login fails with 401, ensure token is cleared
      if (error.message?.includes('Authentication required') || error.message?.includes('401')) {
        console.log('🔄 Login failed with 401, ensuring token is cleared');
        this.removeToken();
      }
      throw error;
    }
  }

  /**
   * Register a new user account
   */
  async register(userData: { 
    username?: string; 
    email?: string; 
    password?: string; 
    phone?: string;
    attribution?: any; // Attribution data from attributionService
    guest?: boolean; // For guest registration
    userType?: 'buyer' | 'seller' | 'bank'; // User type for role assignment (client-side only)
    bankAuthToken?: string; // For bank users
  }): Promise<{ message: string }> {
    // Note: The backend doesn't handle roles at registration
    // Roles are added client-side after successful login
    return this.post<{ message: string }>('/register', userData);
  }

  /**
   * Register and login as a guest user
   */
  async guestLogin(): Promise<{ 
    token: string; 
    userId: string; 
    username: string; 
    isGuest: boolean;
    sessionExpiry: string;
    message: string;
  }> {
    console.log('🚀 Starting guest login API call...');
    
    try {
      // Clear any existing token before guest login
      const existingToken = this.getToken();
      if (existingToken) {
        console.log('🔄 Clearing existing token before guest login');
        this.removeToken();
      }
      
      console.log('📡 Sending guest registration request to /register with guest: true');
      console.log('📡 Request URL will be:', this.baseURL + '/register');
      console.log('📡 Request body:', { guest: true });
      
      const result = await this.post<{
        token: string;
        userId: string;
        username: string;
        isGuest: boolean;
        sessionExpiry: string;
        message: string;
      }>('/register', { guest: true });
      
      console.log('✅ Guest registration response received:', result);
      
      // Set the token for subsequent API calls
      if (result.token) {
        this.setToken(result.token);
        console.log('🔑 Guest token set successfully');
      }
      
      return result;
    } catch (error: any) {
      console.error('❌ Guest login failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response,
        status: error.status,
        stack: error.stack
      });
      
      // Clean up on error
      this.removeToken();
      
      // Provide more specific error messages
      if (error.message?.includes('Network error')) {
        throw new Error('Network error: Unable to connect to the server');
      } else if (error.message?.includes('404')) {
        throw new Error('Guest login endpoint not found');
      } else if (error.message?.includes('500')) {
        throw new Error('Server error during guest registration');
      }
      
      throw error;
    }
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await this.post('/logout');
    } finally {
      this.removeToken();
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<User> {
    return this.get<User>('/profile');
  }

  /**
   * Check if user is currently authenticated
   */
  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  /**
   * Get current authentication token
   */
  getCurrentToken(): string | null {
    return this.getToken();
  }

  /**
   * Request OTP code for login
   */
  async requestOtp(
    identifier: string,
    type: 'email' | 'sms'
  ): Promise<{ message: string; expiresIn?: number; expiresAt?: string }> {
    const endpoint = type === 'sms' ? '/auth/otp/sms/send' : '/auth/otp/email/send';
    const normalized = type === 'email' ? identifier.trim().toLowerCase() : identifier.trim();
    const payload = type === 'sms' ? { phoneNumber: normalized } : { email: normalized };
    return this.post<{ message: string; expiresIn?: number; expiresAt?: string }>(endpoint, payload);
  }

  /**
   * OTP send/resend status for an identifier
   */
  async getOtpStatus(
    type: 'email' | 'sms',
    identifier: string
  ): Promise<{
    hasActiveCode?: boolean;
    canResend?: boolean;
    expiresAt?: string | null;
    verifyLockedUntil?: string | null;
    verifyAttemptsRemaining?: number;
  }> {
    const normalized = type === 'email' ? identifier.trim().toLowerCase() : identifier.trim();
    const encoded = encodeURIComponent(normalized);
    return this.get(`/auth/otp/status/${type}/${encoded}`);
  }

  /**
   * Verify OTP code and login
   */
  async verifyOtp(
    identifier: string,
    code: string,
    type: 'email' | 'sms'
  ): Promise<{ token: string; username: string }> {
    if (!identifier || !code || !type) {
      throw new Error('Missing required fields for OTP verification');
    }

    const normalized = type === 'email' ? identifier.trim().toLowerCase() : identifier.trim();
    const payload = {
      identifier: normalized,
      code: code.trim(),
      type,
    };

    try {
      const result = await this.post<{ token: string; username: string }>('/auth/otp/verify', payload);
      if (result.token) {
        this.setToken(result.token);
      }
      return result;
    } catch (error: unknown) {
      const err = error as Error & { status?: number };
      if (err.status === 401 || err.message?.includes('401')) {
        this.removeToken();
      }
      throw error;
    }
  }

  /**
   * Request password reset email
   * Uses the v1 API endpoint which is handled by the backend
   * The platform determines the redirect URL based on the API key
   */
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    try {
      // Note: The backend returns 201 with empty body on success
      // The platform will determine the redirect URL based on the API key
      // and append the token as a query parameter
      await this.post('/reset-password', { email });
      return { message: 'Password reset email sent successfully. Please check your inbox.' };
    } catch (error) {
      console.error('Password reset request failed:', error);
      throw error;
    }
  }
} 